import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useEnpsStore } from '../store'

/**
 * @fileoverview eNPS Launch Tab
 *
 * Launch surveys and manage active executions
 */

export const EnpsLaunchTab = () => {
	const { organizationId, enpsId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const { enpsConfig, useActivateEnpsConfig, usePauseEnpsConfig } = useEnpsStore()
	const { activateEnpsConfig, isLoading: isActivating } = useActivateEnpsConfig(enpsId as string)
	const { pauseEnpsConfig, isLoading: isPausing } = usePauseEnpsConfig(enpsId as string)
	const toast = useToast()
	const queryClient = useQueryClient()
	const [isLaunching, setIsLaunching] = useState(false)
	const [isClosing, setIsClosing] = useState(false)

	// Fetch active execution
	const { data: activeExecution, isLoading: isLoadingExecution } = useQuery({
		...trpc.admin.enps.getActiveExecution.queryOptions({
			enpsConfigId: enpsId as string,
		}),
		enabled: !!enpsId,
	})

	const launchMutation = useMutation({
		...trpc.admin.enps.launch.mutationOptions(),
		onSuccess: () => {
			toast.showToast('NPS survey launched successfully')
			setIsLaunching(false)
			// Invalidate to refetch active execution
			queryClient.invalidateQueries({
				queryKey: trpc.admin.enps.getActiveExecution.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to launch survey', 'error')
			setIsLaunching(false)
		},
	})

	const closeExecutionMutation = useMutation({
		...trpc.admin.enps.closeExecution.mutationOptions(),
		onSuccess: () => {
			toast.showToast('Survey execution closed successfully')
			setIsClosing(false)
			// Invalidate to refetch active execution
			queryClient.invalidateQueries({
				queryKey: trpc.admin.enps.getActiveExecution.queryKey(),
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.enps.getById.queryKey(),
			})
		},
		onError: (error) => {
			toast.showToast(error.message || 'Failed to close execution', 'error')
			setIsClosing(false)
		},
	})

	const handleLaunch = async () => {
		if (!enpsConfig || !organizationId || !enpsId) return

		if (!enpsConfig.populationId) {
			toast.showToast('Please configure a population first', 'error')
			return
		}

		if (!enpsConfig.emailTemplateId) {
			toast.showToast('Please select an email template first', 'error')
			return
		}

		setIsLaunching(true)
		await launchMutation.mutateAsync({
			enpsConfigId: enpsId,
			organizationId,
			emailTemplateId: enpsConfig.emailTemplateId,
		})
	}

	const handleCloseExecution = async () => {
		if (!activeExecution || !organizationId) return

		setIsClosing(true)
		await closeExecutionMutation.mutateAsync({
			executionId: activeExecution.id,
			organizationId,
		})
	}

	const handleActivate = async () => {
		await activateEnpsConfig()
	}

	const handlePause = async () => {
		await pauseEnpsConfig()
	}

	const isPending = isLaunching || isActivating || isPausing || isClosing || isLoadingExecution

	return (
		<div className='relative pb-24 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='px-6 max-w-4xl space-y-6'>
				<div>
					<Text variant='large' weight='semibold'>
						Survey Status
					</Text>
					<div className='mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4'>
						<div className='grid grid-cols-2 gap-4'>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Status
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{enpsConfig?.status.toUpperCase()}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Cadence
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{enpsConfig?.cadence}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Next Survey
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{enpsConfig?.nextScheduledDate
										? new Date(enpsConfig.nextScheduledDate).toLocaleDateString()
										: 'Not scheduled'}
								</Text>
							</div>
							<div>
								<Text variant='xsmall' className='text-gray-600'>
									Population Configured
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{enpsConfig?.populationId ? 'Yes' : 'No'}
								</Text>
							</div>
						</div>
					</div>
				</div>

				{/* Active Execution Section */}
				{activeExecution && (
					<div>
						<Text variant='medium' weight='semibold'>
							Active Execution
						</Text>
						<div className='mt-4 bg-green-50 border border-green-200 rounded-lg p-4'>
							<div className='grid grid-cols-2 gap-4'>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Launched At
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{new Date(activeExecution.launchedAt).toLocaleString()}
									</Text>
								</div>
								<div>
									<Text variant='xsmall' className='text-gray-600'>
										Status
									</Text>
									<Text variant='small' weight='semibold' className='mt-1'>
										{activeExecution.status.toUpperCase()}
									</Text>
								</div>
							</div>
							<Button variant='destructive' onClick={handleCloseExecution} className='mt-4' disabled={isClosing}>
								{isClosing ? 'Closing...' : 'Close Execution'}
							</Button>
						</div>
					</div>
				)}

				<div className='space-y-3'>
					<Text variant='medium' weight='semibold'>
						Actions
					</Text>

					{enpsConfig?.status === 'draft' && (
						<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<Text variant='small' className='text-blue-900'>
								Activate this survey to enable scheduled sending based on the cadence.
							</Text>
							<Button variant='primary' onClick={handleActivate} className='mt-3'>
								Activate Survey
							</Button>
						</div>
					)}

					{enpsConfig?.status === 'active' && (
						<>
							<div className='bg-green-50 border border-green-200 rounded-lg p-4'>
								<Text variant='small' className='text-green-900'>
									Survey is active and will be sent automatically based on the schedule.
								</Text>
								<Button variant='secondary' onClick={handlePause} className='mt-3'>
									Pause Survey
								</Button>
							</div>

							<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
								<Text variant='small' className='text-yellow-900'>
									{activeExecution
										? 'There is already an active execution. Please close it before launching a new one.'
										: 'Launch an immediate survey to all configured participants.'}
								</Text>
								<Button variant='primary' onClick={handleLaunch} className='mt-3' disabled={!!activeExecution}>
									Launch Survey Now
								</Button>
							</div>
						</>
					)}

					{enpsConfig?.status === 'paused' && (
						<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
							<Text variant='small' className='text-yellow-900'>
								Survey is paused. Activate to resume scheduled sending.
							</Text>
							<Button variant='primary' onClick={handleActivate} className='mt-3'>
								Resume Survey
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}
